import { createSignal, onCleanup, onMount } from "solid-js"
import { readUserSessionToken } from "@/storage/user-session"
import { expirePreferences, setPreference } from "@/transport/preferences"
import { describeError } from "@/utils/error-message"

const invalidationListeners = new Set<() => void>()

/** Runs listener whenever the account's saved settings may have changed elsewhere. */
export function onAccountPreferencesInvalidated(listener: () => void) {
  invalidationListeners.add(listener)
  return () => { invalidationListeners.delete(listener) }
}

/**
 * Reloads every account setting on screen: another device or an agent changed
 * the account. `subscribed` is the fleet event mark of a stream that
 * reconnected: settings read since then are kept (see expirePreferences).
 */
export function invalidateAccountPreferences(subscribed?: string) {
  expirePreferences(subscribed)
  for (const listener of [...invalidationListeners]) listener()
}

const focusListeners = new Set<() => void>()
const focus = () => {
  expirePreferences()
  for (const listener of [...focusListeners]) listener()
}

/**
 * Settings are read again when the window regains focus: they may have changed
 * on another device. One focus expires the shared read once, so every listener
 * shares the next one.
 */
export function onAccountPreferencesFocus(listener: () => void) {
  if (focusListeners.size === 0) window.addEventListener("focus", focus)
  focusListeners.add(listener)
  return () => {
    focusListeners.delete(listener)
    if (focusListeners.size === 0) window.removeEventListener("focus", focus)
  }
}

export function createAccountPreference<T>(key: string, initial: T, load: () => Promise<T>) {
  return createAccountResource(initial, load, async (next: T) => {
    await setPreference(`/api/ai/preferences/${key}`, next)
    return next
  }, (_, next) => next)
}

// Mutations are serialized; optimistic changes are rebased over acknowledged
// state so a failed edit cannot leak into a later successful save.
export function createAccountResource<T, Change>(
  initial: T,
  load: () => Promise<T>,
  write: (change: Change) => Promise<T>,
  optimistic?: (current: T, change: Change) => T,
) {
  const owner = readUserSessionToken()
  const [value, setValue] = createSignal<T>(initial)
  const [ready, setReady] = createSignal(false)
  const [saving, setSaving] = createSignal(false)
  const [error, setError] = createSignal("")
  let confirmed = initial
  let revision = 0
  let disposed = false
  // An invalidation during a load or save, which may predate the change.
  let stale = false
  let queue = Promise.resolve()
  let loading: Promise<void> | undefined
  const pending: Array<{ change: Change }> = []
  const project = () => setValue(() => optimistic ? pending.reduce((value, entry) => optimistic(value, entry.change), confirmed) : confirmed)
  const active = () => !disposed && owner === readUserSessionToken()

  const refresh = () => {
    if (loading) return loading
    if (!active() || saving()) return Promise.resolve()
    const started = revision
    loading = load().then((next) => {
      if (!active() || started !== revision) return
      confirmed = next
      setValue(() => next)
      setReady(true)
      setError("")
    }).catch((cause) => {
      if (active() && started === revision) setError(describeError(cause))
    }).finally(() => {
      loading = undefined
      reloadIfStale()
    })
    return loading
  }
  const reloadIfStale = () => {
    if (!stale || loading || saving()) return
    stale = false
    void refresh()
  }
  const invalidate = () => {
    stale = true
    reloadIfStale()
  }

  const save = (change: Change) => {
    if (!active() || !ready()) return Promise.reject(new Error("Load account settings before editing them"))
    const started = ++revision
    const entry = { change }
    pending.push(entry)
    project()
    setSaving(true)
    setError("")
    const operation = queue.then(async () => {
      if (owner !== readUserSessionToken()) throw new Error("Account changed while saving settings")
      const next = await write(change)
      if (active()) confirmed = next
    }).catch((cause) => {
      if (active() && started === revision) {
        setError(describeError(cause))
      }
      throw cause
    }).finally(() => {
      pending.splice(pending.indexOf(entry), 1)
      if (active()) {
        project()
        setSaving(pending.length > 0)
        reloadIfStale()
      }
    })
    queue = operation.catch(() => undefined)
    return operation
  }

  let stopInvalidation: (() => void) | undefined
  let stopFocus: (() => void) | undefined
  onMount(() => {
    void refresh()
    stopFocus = onAccountPreferencesFocus(() => void refresh())
    stopInvalidation = onAccountPreferencesInvalidated(invalidate)
  })
  onCleanup(() => {
    disposed = true
    stopFocus?.()
    stopInvalidation?.()
  })
  return { value, ready, saving, error, refresh, save }
}
